// src/stores/payrollDeductionStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import PayrollDeductionService from "@/services/payrollDeduction.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePayrollDeductionStore = defineStore("payrollDeduction", () => {
  const deductions = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const isLoading = ref(false);

  const fetchAll = async () => {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_PAYROLL))) {
      deductions.value = [];
      return;
    }

    loading.value = true;
    try {
      const res = await PayrollDeductionService.getAll();

      // handle both: axios response OR already-unwrapped data
      const data = Array.isArray(res) ? res : res?.data ?? [];

      deductions.value = data;
      error.value = null;
    } catch (err) {
      error.value =
        err?.response?.data?.message || "Failed to load deductions";
      deductions.value = []; // safe fallback
    } finally {
      loading.value = false;
    }
  };

  const createDeduction = async (payload) => {
    isLoading.value = true;
    try {
      const res = await PayrollDeductionService.create(payload);
      deductions.value.push(res.data);
      toast("Payroll deduction created successfully!");
      return res.data;
    } catch (err) {
      error.value =
        err.response?.data?.message || "Failed to create payroll deduction";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateDeduction = async (id, payload) => {
    isLoading.value = true;
    try {
      const res = await PayrollDeductionService.update(id, payload);
      const index = deductions.value.findIndex((d) => d.id === id);

      if (index !== -1) {
        deductions.value[index] = res.data;
      }

      toast("Payroll deduction updated successfully!");
      return res.data;
    } catch (err) {
      error.value =
        err.response?.data?.message || "Failed to update payroll deduction";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteDeduction = async (id) => {
    isLoading.value = true;
    try {
      await PayrollDeductionService.delete(id);
      deductions.value = deductions.value.filter((d) => d.id !== id);
      toast("Payroll deduction deleted successfully!");
    } catch (err) {
      error.value =
        err.response?.data?.message || "Failed to delete payroll deduction";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    deductions,
    loading,
    isLoading,
    error,
    fetchAll,
    createDeduction,
    updateDeduction,
    deleteDeduction,
  };
});
